import { Separator } from '@monksflow/monks-ui';

import type { ExternalToolCategoryDto, ExternalToolDto } from '@inference.engine/api-client/web';

import {
  AgentToolBasicInformation,
  type AgentToolCategoryOption,
} from '@/features/admin/components/AgentToolBasicInformation';
import { useIsCategoryPickerEnabled } from '@/shared/hooks/useIsCategoryPickerEnabled';
import type { CategoryId } from '@/shared/constants/category-colors';
import { OverviewCard } from './OverviewCard';
import { useTranslation } from 'react-i18next';

type Tab = 'overview' | 'tools' | 'categories';

interface ExternalToolsOverviewProps {
  tools: ExternalToolDto[];
  allCategories: ExternalToolCategoryDto[];
  toolsLoading: boolean;
  categoriesLoading: boolean;
  onNavigate: (tab: Tab) => void;
  toolName?: string;
  toolDescription?: string;
  onSaveBasicInfo?: (
    data: { name: string; description: string; category?: CategoryId },
    workspaceId?: string,
  ) => Promise<void>;
  workspaceId?: string;
  category?: CategoryId;
  categoryOptions?: readonly AgentToolCategoryOption[];
}

export const ExternalToolsOverview = ({
  tools,
  allCategories,
  toolsLoading,
  categoriesLoading,
  onNavigate,
  toolName = '',
  toolDescription = '',
  onSaveBasicInfo,
  workspaceId,
  category,
  categoryOptions,
}: ExternalToolsOverviewProps) => {
  const { t } = useTranslation('admin');

  const isCategoryPickerEnabled = useIsCategoryPickerEnabled();
  const showCategory = isCategoryPickerEnabled && !!category && !!categoryOptions;
  const configuredCount = (tools.length > 0 ? 1 : 0) + (allCategories.length > 0 ? 1 : 0);

  return (
    <>
      <AgentToolBasicInformation
        key={workspaceId ?? '__org__'}
        toolName={toolName}
        toolDescription={toolDescription}
        onSaveBasicInfo={onSaveBasicInfo}
        workspaceId={workspaceId}
        fallbackName={t('admin.externalToolsOverview.externalTools')}
        fallbackDescription={t('admin.externalToolsOverview.getTheInfoYouNeed')}
        category={showCategory ? category : undefined}
        categoryOptions={showCategory ? categoryOptions : undefined}
      />

      <Separator decorative={false} />

      {/* Setup checklist */}
      <div className="flex flex-col px-6 py-5">
        <h3 className="text-xl leading-7 text-foreground">
          {t('admin.externalToolsOverview.setupChecklist')}
        </h3>
        <p className="mt-1 text-xs leading-4 text-muted-foreground">
          {t('admin.externalToolsOverview.ofSectionsConfigured', { configuredCount })}
        </p>
      </div>
      <Separator decorative={false} />
      <div className="flex flex-col p-2">
        <OverviewCard
          label={t('admin.externalToolsOverview.externalTools')}
          count={tools.length}
          isLoading={toolsLoading}
          onNavigate={() => onNavigate('tools')}
        />
        <OverviewCard
          label={t('admin.externalToolsOverview.categories')}
          count={allCategories.length}
          isLoading={categoriesLoading}
          onNavigate={() => onNavigate('categories')}
        />
      </div>
    </>
  );
};
